import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { 
  ArrowLeft,
  Building2,
  Users,
  UserPlus,
  Edit,
  Trash2,
  Sun,
  Moon,
  Crown,
  User,
  Mail,
  Globe,
  MapPin,
  Lock,
  Search,
  Send,
  MoreVertical,
  Sunrise,
  Sunset,
  UserX,
  UserCheck,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyManagement = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  
  // Unassigned users state
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [filteredUnassignedUsers, setFilteredUnassignedUsers] = useState([]);
  const [unassignedSearchQuery, setUnassignedSearchQuery] = useState('');
  const [assigningUser, setAssigningUser] = useState(null);
  
  // Modal states
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    timezone: 'UTC',
    location: ''
  });

  const checkSuperUser = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/is-super-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsSuperUser(data.is_super_user);
        return data;
      }
    } catch (error) {
      console.error('Error checking super user status:', error);
    }
    return { is_super_user: false };
  }, [token]);

  const fetchCompany = useCallback(async () => {
    try {
      const response = await fetch(`${API}/companies/my-company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    }
    return null;
  }, [token]);

  const fetchCompanyUsers = useCallback(async (companyId) => {
    try {
      const response = await fetch(`${API}/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchTimezones = useCallback(async () => {
    try {
      const response = await fetch(`${API}/timezones`);
      if (response.ok) {
        const data = await response.json();
        setTimezones(data.timezones || []);
      }
    } catch (error) {
      console.error('Error fetching timezones:', error);
    }
  }, []);

  const fetchUnassignedUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/companies/unassigned-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnassignedUsers(data);
        setFilteredUnassignedUsers(data);
      }
    } catch (error) {
      console.error('Error fetching unassigned users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const superUserData = await checkSuperUser();
      
      if (!superUserData.is_super_user) {
        toast.error('Access denied. Super user privileges required.');
        navigate('/');
        return;
      }
      
      const companyData = await fetchCompany();
      if (companyData) {
        await fetchCompanyUsers(companyData.id);
      }
      await fetchTimezones();
      await fetchUnassignedUsers();
      setLoading(false);
    };
    loadData();
  }, [checkSuperUser, fetchCompany, fetchCompanyUsers, fetchTimezones, fetchUnassignedUsers, navigate]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(users.filter(u => 
        u.name.toLowerCase().includes(query) || 
        u.email.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, users]);

  // Filter unassigned users based on search query
  useEffect(() => {
    if (!unassignedSearchQuery.trim()) {
      setFilteredUnassignedUsers(unassignedUsers);
    } else {
      const query = unassignedSearchQuery.toLowerCase();
      setFilteredUnassignedUsers(unassignedUsers.filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query)
      ));
    }
  }, [unassignedSearchQuery, unassignedUsers]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      timezone: 'UTC',
      location: ''
    });
    setSelectedUser(null);
  };

  const handleAddUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Name, email, and password are required');
      return;
    }

    try {
      const response = await fetch(`${API}/companies/${company.id}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('User created successfully');
        await fetchCompanyUsers(company.id);
        setShowAddUser(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    const updateData = {
      name: formData.name || undefined,
      email: formData.email || undefined,
      timezone: formData.timezone || undefined,
      location: formData.location || undefined,
      password: formData.password || undefined
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined || updateData[key] === '') {
        delete updateData[key];
      }
    });

    try {
      const response = await fetch(`${API}/companies/${company.id}/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('User updated successfully');
        await fetchCompanyUsers(company.id);
        setShowEditUser(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${API}/companies/${company.id}/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('User removed from company');
        await fetchCompanyUsers(company.id);
        setShowDeleteConfirm(false);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove user');
      }
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleAssignUser = async () => {
    if (!assigningUser || !company) return;

    try {
      const response = await fetch(`${API}/companies/${company.id}/assign-user/${assigningUser.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`${assigningUser.name} has been added to ${company.name}`);
        // Refresh both lists
        await fetchCompanyUsers(company.id);
        await fetchUnassignedUsers();
        setShowAssignConfirm(false);
        setAssigningUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign user');
      }
    } catch (error) {
      toast.error('Failed to assign user');
    }
  };

  const openAssignModal = (userToAssign) => {
    setAssigningUser(userToAssign);
    setShowAssignConfirm(true);
  };

  const openEditModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      timezone: userToEdit.timezone || 'UTC',
      location: userToEdit.location || ''
    });
    setShowEditUser(true);
  };

  const openDeleteModal = (userToDelete) => {
    setSelectedUser(userToDelete);
    setShowDeleteConfirm(true);
  };

  const sendWelcomeEmail = async (targetUser) => {
    try {
      const response = await fetch(`${API}/admin/send-test-email?email=${encodeURIComponent(targetUser.email)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Welcome email sent to ${targetUser.name}`);
      } else {
        toast.error(data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send welcome email');
    }
  };

  const sendSummaryEmail = async (targetUser, reportType) => {
    try {
      const response = await fetch(
        `${API}/admin/send-daily-summary?email=${encodeURIComponent(targetUser.email)}&report_type=${reportType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${reportType} summary sent to ${targetUser.name}`, {
          description: `${data.stats?.new_tasks || 0} tasks, ${data.stats?.new_messages || 0} messages`
        });
      } else {
        toast.error(data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error(`Failed to send ${reportType} summary`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading company management...</div>
      </div>
    );
  }

  if (!isSuperUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Access denied</div>
      </div>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="px-4 sm:px-6 py-3 sm:py-4 pl-14 lg:pl-6">
            <h1 className="font-heading text-xl sm:text-2xl font-bold">{company?.name || 'Company'}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">{users.length} team members</p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-purple-500/10">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{company?.name}</p>
                <p className="text-muted-foreground text-xs sm:text-sm">Company</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-muted-foreground text-sm">Team Members</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${unassignedUsers.length > 0 ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
                <UserX className={`w-8 h-8 ${unassignedUsers.length > 0 ? 'text-orange-500' : 'text-green-500'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{unassignedUsers.length}</p>
                <p className="text-muted-foreground text-sm">Unassigned</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Crown className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold truncate max-w-[120px]">{user?.name}</p>
                <p className="text-muted-foreground text-sm">Super User</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed User Management */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="members" data-testid="tab-members" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Team Members
              <Badge variant="secondary" className="ml-1">{users.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="unassigned" data-testid="tab-unassigned" className="flex items-center gap-2">
              <UserX className="w-4 h-4" />
              Unassigned
              {unassignedUsers.length > 0 && (
                <Badge variant="destructive" className="ml-1">{unassignedUsers.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team Members
                </CardTitle>
                <Button onClick={() => { resetForm(); setShowAddUser(true); }} data-testid="add-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-users-input"
                  />
                </div>

                {/* User List */}
                <div className="space-y-3">
                  {filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No users match your search' : 'No users in company'}
                    </div>
                  ) : (
                    filteredUsers.map((companyUser) => (
                      <div
                        key={companyUser.id}
                        className="flex items-center justify-between p-4 rounded-xl border bg-card hover:bg-muted/50 transition-colors"
                        data-testid={`user-row-${companyUser.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className={companyUser.role === 'super_user' ? 'bg-amber-500 text-white' : ''}>
                              {companyUser.name?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{companyUser.name}</p>
                              {companyUser.role === 'super_user' && (
                                <Badge className="bg-amber-500/20 text-amber-600 dark:text-amber-400">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Super User
                                </Badge>
                              )}
                              {companyUser.id === user?.id && companyUser.role !== 'super_user' && (
                                <Badge variant="outline">You</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{companyUser.email}</p>
                            {companyUser.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {companyUser.location}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {/* Email Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                data-testid={`email-menu-${companyUser.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Send Email</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => sendWelcomeEmail(companyUser)}
                                data-testid={`send-welcome-${companyUser.id}`}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Welcome Email
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => sendSummaryEmail(companyUser, 'Morning')}
                                data-testid={`send-morning-${companyUser.id}`}
                              >
                                <Sunrise className="w-4 h-4 mr-2" />
                                Morning Summary
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => sendSummaryEmail(companyUser, 'Afternoon')}
                                data-testid={`send-afternoon-${companyUser.id}`}
                              >
                                <Sunset className="w-4 h-4 mr-2" />
                                Afternoon Summary
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditModal(companyUser)}
                            data-testid={`edit-user-${companyUser.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {companyUser.role !== 'super_user' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => openDeleteModal(companyUser)}
                              data-testid={`delete-user-${companyUser.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unassigned Users Tab */}
          <TabsContent value="unassigned">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <UserX className="w-5 h-5" />
                  Unassigned Users
                  {unassignedUsers.length > 0 && (
                    <Badge variant="destructive" className="ml-2">{unassignedUsers.length} pending</Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Users who have registered but are not assigned to any company
                </p>
              </CardHeader>
              <CardContent>
                {/* Info Alert */}
                {unassignedUsers.length > 0 && (
                  <div className="flex items-start gap-3 p-4 mb-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-orange-600 dark:text-orange-400">Action Required</p>
                      <p className="text-sm text-muted-foreground">
                        These users registered with email addresses that don't match any existing company domain. 
                        Assign them to your company to enable task management.
                      </p>
                    </div>
                  </div>
                )}

                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search unassigned users by name or email..."
                    value={unassignedSearchQuery}
                    onChange={(e) => setUnassignedSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-unassigned-input"
                  />
                </div>

                {/* Unassigned User List */}
                <div className="space-y-3">
                  {filteredUnassignedUsers.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">
                        {unassignedSearchQuery ? 'No users match your search' : 'All users are assigned!'}
                      </p>
                      {!unassignedSearchQuery && (
                        <p className="text-sm mt-1">There are no pending users to assign.</p>
                      )}
                    </div>
                  ) : (
                    filteredUnassignedUsers.map((unassignedUser) => (
                      <div
                        key={unassignedUser.id}
                        className="flex items-center justify-between p-4 rounded-xl border bg-card hover:bg-muted/50 transition-colors"
                        data-testid={`unassigned-user-row-${unassignedUser.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-orange-500/20 text-orange-600">
                              {unassignedUser.name?.charAt(0).toUpperCase() || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{unassignedUser.name || 'Unknown'}</p>
                              <Badge variant="outline" className="text-orange-500 border-orange-500/30">
                                Unassigned
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{unassignedUser.email}</p>
                            {unassignedUser.created_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Registered: {new Date(unassignedUser.created_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => openAssignModal(unassignedUser)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`assign-user-${unassignedUser.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add to {company?.name}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add User Modal */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account for your company
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                data-testid="add-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
                data-testid="add-user-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password *
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="add-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Timezone
              </Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
              >
                <SelectTrigger data-testid="add-user-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="New York, USA"
                data-testid="add-user-location"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} data-testid="confirm-add-user">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update user information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Full Name
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="edit-user-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                New Password (leave empty to keep current)
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="edit-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Timezone
              </Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
              >
                <SelectTrigger data-testid="edit-user-timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                data-testid="edit-user-location"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditUser} data-testid="confirm-edit-user">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Remove User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{selectedUser?.name}</strong> from the company?
              This will not delete their account, but they will no longer be part of {company?.name}.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} data-testid="confirm-delete-user">
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Confirmation Modal */}
      <Dialog open={showAssignConfirm} onOpenChange={setShowAssignConfirm}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <UserPlus className="w-5 h-5" />
              Add User to Company
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to add <strong>{assigningUser?.name}</strong> ({assigningUser?.email}) to {company?.name}?
              They will become a team member and can be assigned tasks.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignConfirm(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700" 
              onClick={handleAssignUser} 
              data-testid="confirm-assign-user"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Add to Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Sidebar>
  );
};

export default CompanyManagement;
